import { S as Swiper } from './classes-to-selector-6ijYtnfY.js';
import { N as Navigation } from './navigation-D3ssu5k4.js';
import { P as Pagination } from './pagination-6ibxO3Ub.js';
import { S as Scrollbar } from './scrollbar-CigCyCA3.js';
import { A as Autoplay } from './autoplay-C12qvRhh.js';
import { M as Manipulation } from './manipulation-DUnmDJcO.js';

/**
 * @typedef {Object} SwiperOptions
 * @property {Array<Object>} modules - Swiper modules to use
 * @property {number} threshold - Touch threshold
 * @property {boolean} [grid_on_mobile] - Whether to show grid on mobile
 * @property {boolean} [loop] - Whether swiper should loop
 * @property {Object} [navigation] - Navigation configuration
 */

/**
 * @typedef {Object} ShopifyEvent
 * @property {HTMLElement} target - Event target element
 * @property {Object} target.dataset - Dataset of the target element
 * @property {string} [target.dataset.swiperSlideIndex] - Slide index for swiper
 */

/**
 * Featured Collections Web Component
 * Handles dynamic collection switching with Swiper integration for product carousels
 *
 * @class featuredCollections
 * @extends {HTMLElement}
 */
class featuredCollections extends HTMLElement {
  /**
   * Private AbortController for managing fetch requests
   * @type {AbortController}
   * @private
   */
  #controller = new AbortController();

  /**
   * The swiper container element
   * @type {HTMLElement|null}
   */
  swiper = this.querySelector('[data-swiper]');

  /**
   * The Swiper instance
   * @type {Swiper|null}
   */
  swiperInstance = null;

  /**
   * Media query for mobile detection
   * @type {MediaQueryList}
   */
  mediaQuery = window.matchMedia('(max-width: 767px)');

  /**
   * Collection button element for navigation
   * @type {HTMLElement|false}
   */
  collectionButton = false;

  /**
   * Maximum number of products to display
   * @type {number}
   */
  maxProducts = 3;

  /**
   * Maximum number of slides (products + 1)
   * @type {number}
   */
  maxSlides = 4;

  /**
   * Swiper configuration options
   * @type {SwiperOptions}
   */
  swiperOptions = {
    modules: [Navigation, Pagination, Scrollbar, Autoplay, Manipulation],
    threshold: 10,
  };

  /**
   * @description Initializes the featured collections component. Sets up swiper, event listeners, and collection navigation
   */
  connectedCallback() {
    // Stop when the swiper is not found
    if (!this.swiper) return;

    // Check if we have extra options on the HTML
    if (this.swiper.dataset.options) {
      const options = JSON.parse(this.swiper.dataset.options || '{}');
      this.swiperOptions = {
        ...this.swiperOptions,
        ...options,
      };
    }

    // Initialize swiper based on mobile_grid setting and screen size
    this.initializeSwiper();

    // Listen for resize events to handle mobile/desktop transitions
    this.handleResize();

    // Initialize component properties
    this.collectionButton = this.querySelector('[data-collection-url]') || false;
    this.maxProducts = parseInt(this.swiper.dataset.products) || 3;
    this.maxSlides = this.maxProducts + 1;

    // Event listener collection navigation clicks
    const collectionNavigation = this.querySelectorAll('[data-collection-handle]');
    collectionNavigation.forEach((item) => {
      const handle = item.dataset.collectionHandle || false;

      if (!handle) return;

      item.addEventListener('click', async (event) => {
        this.classList.add('loading');
        this.renderCollectionButton(handle);
        this.getCollectionProducts(handle);

        // Handles active status
        const activeBefore = this.querySelector('.featured-collections__nav__item.active') || false;
        if (activeBefore) activeBefore.classList.remove('active');
        event.target.classList.add('active');
      });
    });

    this.shopifyDesignMode();
  }

  /**
   * @method initializeSwiper
   * @description Initialize swiper based on mobile_grid setting and screen size. Destroys existing swiper instance if it exists and creates a new one when needed
   * @returns {void}
   */
  initializeSwiper() {
    const isMobile = this.mediaQuery.matches;
    const gridOnMobile = this.swiperOptions.grid_on_mobile || false;

    // Destroy existing swiper if it exists
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    // Only create swiper if:
    // - Not mobile, OR
    // - Mobile but grid_on_mobile is false
    if (!isMobile || !gridOnMobile) {
      this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);
    }
  }

  /**
   * @method handleResize
   * @description Handle media query changes to reinitialize swiper when needed. Listens for viewport size changes and reinitializes swiper accordingly
   * @returns {void}
   */
  handleResize() {
    this.mediaQuery.addEventListener('change', () => {
      this.initializeSwiper();
    });
  }

  /**
   * @method renderCollectionButton
   * @description Updates the collection button href to point to the specified collection
   * @param {string|false} [handle=false] - The collection handle to link to
   * @returns {void}
   */
  renderCollectionButton(handle = false) {
    if (handle && this.collectionButton)
      this.collectionButton.href = `${window?.Shopify?.routes?.root || '/'}collections/${handle}/`;
  }

  /**
   * @method getCollectionProducts
   * @description Fetches and displays products from a specific collection. Makes an AJAX request to get collection products and updates the swiper slides
   * @param {string|false} [handle=false] - The collection handle to fetch products from
   * @returns {Promise<void>} Promise that resolves when products are loaded
   * @throws {Error} When fetch request fails or is aborted
   */
  async getCollectionProducts(handle = false) {
    if (!handle) return;

    try {
      this.#controller.abort();
      this.#controller = new AbortController();

      const response = await fetch(
        `${
          window?.Shopify?.routes?.root || '/'
        }collections/${handle}?section_id=fetch-featured-collection`,
        { signal: this.#controller.signal }
      );

      const textResponse = await response.text();
      const html = new DOMParser().parseFromString(textResponse, 'text/html');
      const nodeList = html.querySelectorAll('.swiper-slide');
      const slideList = Array.from(nodeList);
      if (slideList.length > 0 === false) return;

      if (slideList.length > this.maxSlides)
        slideList.splice(this.maxProducts, slideList.length - this.maxSlides);

      // Only manipulate swiper if instance exists
      if (this.swiperInstance) {
        this.swiperInstance.removeAllSlides();
        this.swiperInstance.appendSlide(slideList);
      } else {
        // If no swiper instance, directly replace the slides in the DOM
        const swiperWrapper = this.swiper.querySelector('.swiper-wrapper');
        if (swiperWrapper) {
          swiperWrapper.innerHTML = '';
          slideList.forEach((slide) => swiperWrapper.appendChild(slide));
        }
      }
    } catch (error) {
      console.error('Error fetching collection products:', error);
    } finally {
      this.classList.remove('loading');
    }
  }

  /**
   * @method shopifyDesignMode
   * @description Sets up event listeners for Shopify theme editor interactions. Handles section load, block select, and block deselect events in design mode
   * @returns {void}
   */
  shopifyDesignMode() {
    if (!Shopify.designMode) return;

    // Update the swiper when the section event is triggered
    window.addEventListener('shopify:section:load', (event) => {
      if (this.swiperInstance) {
        this.swiperInstance.update();
        if (this.swiperOptions.navigation) {
          this.swiperInstance.navigation.init();
          this.swiperInstance.navigation.update();
        }
      }
    });

    // When on block select go to the slide in front-end
    window.addEventListener('shopify:block:select', (event) => {
      if (this.swiperInstance) {
        this.swiperInstance.update();
        if (this.swiperOptions.navigation) {
          this.swiperInstance.navigation.init();
          this.swiperInstance.navigation.update();
        }
      }
      this.handleBlockSelect(event);
    });

    // When on block deselect go to the slide in front-end
    window.addEventListener('shopify:block:deselect', (event) => {
      if (this.swiperInstance) {
        this.swiperInstance.update();
        if (this.swiperOptions.navigation) {
          this.swiperInstance.navigation.init();
          this.swiperInstance.navigation.update();
        }
      }
      this.handleBlockSelect(event);
    });
  }

  /**
   * @method handleBlockSelect
   * @description Handles theme editor block selection events. Navigates to the appropriate slide when a block is selected in the editor
   * @param {ShopifyEvent} event - The Shopify block select event with target element containing swiperSlideIndex in dataset
   * @returns {void}
   */
  handleBlockSelect(event) {
    // Only handle block select if swiper instance exists
    if (!this.swiperInstance) {
      return;
    }

    // Check if the slide index is set
    if (!('swiperSlideIndex' in event.target.dataset)) {
      return;
    }

    // Set the slide index based on loop settings or not
    let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
    if (this.swiperOptions.loop) {
      swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;
    }

    // Slide to slide based on the data attribute from the target
    this.swiperInstance.slideTo(swipeToSlideIndex, 1000);
  }
}

// Register the custom element if not already defined
/**
 * Global reference to the featuredCollections class
 * @global
 * @type {typeof featuredCollections}
 */
customElements.define('featured-collections', featuredCollections);
